// src/components/WeekNavigator.jsx
import React, { useState, useEffect, useRef } from 'react';
import Button from './ui/Button'; // Assuming Button can be used for icon wrapping
import WeekSelectionModal from './WeekSelectionModal'; // Import the renamed WeekSelectionModal component
import { getWeekNumber } from '../utils/helpers.ts'; // Import getWeekNumber utility
import { useDeviceLayout } from '../contexts/DeviceLayoutContext';

// Placeholder Calendar Icon
const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const PrevWeekIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
);

const NextWeekIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
);

function WeekNavigator({
    currentWeekLabel,
    onCalendarIconClick,
    onPrevWeek,
    onNextWeek,
    currentDate,
    onWeekSelect,
    // New: time mode toggle and semester navigation
    timeMode,
    onTimeModeChange,
    currentPeriodLabel,
    currentPeriodIndex,
    totalPeriods,
    onPrevPeriod,
    onNextPeriod
}) {
    const { isMobileLayout: isMobile } = useDeviceLayout();
    // State to manage the week selection modal
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Default label if none provided
    const displayLabel = currentWeekLabel || "Current Week";

    // Calculate week number for tooltip
    const weekNumber = currentDate ? getWeekNumber(currentDate) : null;

    const handleIconClick = () => {
        if (typeof onCalendarIconClick === 'function') {
            onCalendarIconClick(); // Call the passed handler if provided
        }
        // Open the week selection modal
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleWeekSelect = (selectedDate) => {
        if (typeof onWeekSelect === 'function') {
            onWeekSelect(selectedDate);
        }
        setIsModalOpen(false);
    };

    // Subtle bounce when the displayed calendar week changes (arrows, modal, or edge pull).
    const prevWeekNavKeyRef = useRef(null);
    const [weekNavBounce, setWeekNavBounce] = useState(false);

    useEffect(() => {
        if (!currentDate) {
            prevWeekNavKeyRef.current = null;
            return;
        }
        const key = `${currentDate.getFullYear()}-${getWeekNumber(currentDate)}`;
        const prev = prevWeekNavKeyRef.current;
        prevWeekNavKeyRef.current = key;
        if (prev !== null && prev !== key) {
            setWeekNavBounce(true);
            const t = window.setTimeout(() => setWeekNavBounce(false), 480);
            return () => window.clearTimeout(t);
        }
    }, [currentDate]);

    useEffect(() => {
        if (isMobile && (timeMode === 'semester' || timeMode === 'occupancy') && typeof onTimeModeChange === 'function') {
            onTimeModeChange('weeks');
        }
    }, [isMobile, timeMode, onTimeModeChange]);
    const effectiveMode = isMobile ? 'weeks' : timeMode;
    /** Events-only primary view uses week navigation; underline Weeks like calendar week mode */
    const modeForToggle = effectiveMode === 'events' ? 'weeks' : effectiveMode;

    return (
        <>
            {/* Mode toggle */}
            {onTimeModeChange && !isMobile && (
                <div className="flex items-center justify-center space-x-4 mb-1">
                    <span
                        className={`cursor-pointer text-sm ${modeForToggle === 'weeks' ? 'underline font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => onTimeModeChange && onTimeModeChange('weeks')}
                    >
                        Weeks
                    </span>
                    <span
                        className={`cursor-pointer text-sm ${modeForToggle === 'semester' ? 'underline font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => !isMobile && onTimeModeChange && onTimeModeChange('semester')}
                        title={isMobile ? 'Semester view disabled on mobile' : 'Semester'}
                    >
                        Semester
                    </span>
                </div>
            )}

            {/* Selector based on mode */}
            {effectiveMode === 'semester' || effectiveMode === 'occupancy' ? (
                <div className="flex justify-center items-center gap-1 md:gap-2 mb-0 md:mb-2 h-8 md:h-10">
                    <Button
                        variant="ghost"
                        onClick={onPrevPeriod}
                        className="p-1 md:p-1.5 rounded-full text-gray-600 hover:text-blue-600"
                        title={effectiveMode === 'semester' ? 'Previous Semester' : 'Previous Period'}
                        aria-label={effectiveMode === 'semester' ? 'Previous Semester' : 'Previous Period'}
                        disabled={!(typeof currentPeriodIndex === 'number' && typeof totalPeriods === 'number') || currentPeriodIndex <= 0}
                    >
                        <PrevWeekIcon />
                    </Button>
                    <div className="flex items-center gap-1 p-1 md:p-1.5 rounded-md min-w-[200px] md:min-w-[230px] max-w-[200px] md:max-w-[230px] justify-center h-8 md:h-10" title={currentPeriodLabel || ''}>
                        {/* Invisible icon to keep height consistent with week mode */}
                        <span className="opacity-0 scale-75 md:scale-100"><CalendarIcon /></span>
                        <p className="text-base md:text-lg font-medium text-gray-800 truncate text-center">{currentPeriodLabel || 'No semester'}</p>
                    </div>
                    <Button
                        variant="ghost"
                        onClick={onNextPeriod}
                        className="p-1 md:p-1.5 rounded-full text-gray-600 hover:text-blue-600"
                        title={effectiveMode === 'semester' ? 'Next Semester' : 'Next Period'}
                        aria-label={effectiveMode === 'semester' ? 'Next Semester' : 'Next Period'}
                        disabled={!(typeof currentPeriodIndex === 'number' && typeof totalPeriods === 'number') || currentPeriodIndex >= (totalPeriods - 1)}
                    >
                        <NextWeekIcon />
                    </Button>
                </div>
            ) : (
                <div
                    className={`flex justify-center items-center gap-1 md:gap-2 mb-0 md:mb-2 h-8 md:h-10 ${weekNavBounce ? 'animate-week-nav-bounce' : ''}`}
                >
                    <Button
                        variant="ghost"
                        onClick={onPrevWeek}
                        className="p-1 md:p-1.5 rounded-full text-gray-600 hover:text-blue-600"
                        title="Previous Week"
                        aria-label="Previous Week"
                        disabled={!onPrevWeek}
                    >
                        <PrevWeekIcon />
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={handleIconClick}
                        className="flex items-center gap-1 p-1 md:p-1.5 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 min-w-[200px] md:min-w-[230px] max-w-[200px] md:max-w-[230px] justify-center h-8 md:h-10"
                        title={weekNumber ? `Week ${weekNumber} - Select Week` : 'Select Week'}
                        aria-label={weekNumber ? `Week ${weekNumber} - Select Week` : 'Select Week'}
                    >
                        <div className="scale-75 md:scale-100 flex items-center"><CalendarIcon /></div>
                        <p className="text-base md:text-lg font-medium truncate">{displayLabel}</p>
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={onNextWeek}
                        className="p-1 md:p-1.5 rounded-full text-gray-600 hover:text-blue-600"
                        title="Next Week"
                        aria-label="Next Week"
                        disabled={!onNextWeek}
                    >
                        <NextWeekIcon />
                    </Button>
                </div>
            )}

            {/* Week Selection Modal (weeks mode only, but safe to leave mounted) */}
            <WeekSelectionModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                currentDate={currentDate}
                onWeekSelect={handleWeekSelect}
            />
        </>
    );
}

export default WeekNavigator;